import { useEffect, useState } from 'react';

/**
 * Liest die praegende Farbe eines Albumcovers aus.
 *
 * Warum ueberhaupt: Ein erfundener Farbverlauf sieht bei jedem Song
 * gleich aus und ist genau die Sorte Deko, die eine Oberflaeche
 * beliebig wirken laesst. Die Farbe aus dem Cover kommt dagegen aus
 * echtem Inhalt -- jede Runde sieht anders aus, und zwar passend zum
 * Song, der gerade aufgedeckt wurde.
 *
 * Nur beim Aufdecken einsetzen: Waehrend der Song geheim laeuft, waere
 * eine markante Coverfarbe ein Hinweis auf die Loesung.
 *
 * Spotify liefert die Bilder mit `Access-Control-Allow-Origin: *`
 * (nachgemessen), das Auslesen der Pixel ist also erlaubt. Schlaegt es
 * trotzdem fehl, liefert der Hook `null` und der Aufrufer bleibt bei
 * der Signaturfarbe.
 */

/** Kantenlaenge der Miniatur. 16x16 genuegt fuer eine Grundstimmung. */
const SAMPLE_SIZE = 16;

interface Hsl {
  h: number;
  s: number;
  l: number;
}

function rgbToHsl(r: number, g: number, b: number): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const delta = max - min;

  if (delta === 0) return { h: 0, s: 0, l };

  const s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  let h: number;
  if (max === rn) h = ((gn - bn) / delta + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / delta + 2) / 6;
  else h = ((rn - gn) / delta + 4) / 6;

  return { h: h * 360, s, l };
}

/**
 * Mittelt Farbtoene korrekt ueber den Kreis.
 *
 * Ein arithmetisches Mittel aus 350 Grad und 10 Grad ergaebe 180 --
 * also Cyan statt Rot. Deshalb ueber Vektoren.
 */
function circularMeanHue(samples: Hsl[]): number {
  let x = 0;
  let y = 0;
  for (const { h, s } of samples) {
    const rad = (h * Math.PI) / 180;
    // Nach Saettigung gewichten: blasse Pixel sollen den Ton nicht ziehen.
    x += Math.cos(rad) * s;
    y += Math.sin(rad) * s;
  }
  const angle = (Math.atan2(y, x) * 180) / Math.PI;
  return (angle + 360) % 360;
}

function extract(image: HTMLImageElement): string | null {
  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE_SIZE;
  canvas.height = SAMPLE_SIZE;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;

  context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

  // Wirft bei fehlenden CORS-Headern eine SecurityError.
  const { data } = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

  const usable: Hsl[] = [];
  for (let i = 0; i < data.length; i += 4) {
    if ((data[i + 3] ?? 255) < 200) continue;

    const hsl = rgbToHsl(data[i] ?? 0, data[i + 1] ?? 0, data[i + 2] ?? 0);

    // Fast-Schwarz, fast-Weiss und Graustufen tragen keine Stimmung.
    if (hsl.l < 0.12 || hsl.l > 0.92 || hsl.s < 0.15) continue;
    usable.push(hsl);
  }

  // Reine Graustufen-Cover gibt es -- dann lieber gar keine Faerbung.
  if (usable.length < 8) return null;

  const hue = Math.round(circularMeanHue(usable));
  const saturation = usable.reduce((sum, entry) => sum + entry.s, 0) / usable.length;

  /**
   * Helligkeit und Saettigung werden festgelegt statt uebernommen.
   *
   * Sonst waere die Faerbung bei einem hellen Cover grell und bei einem
   * dunklen unsichtbar. So bleibt der Kontrast zur Schrift verlaesslich
   * -- die Farbe traegt die Stimmung, nicht die Lesbarkeit.
   */
  const clamped = Math.min(0.72, Math.max(0.4, saturation));
  return `hsl(${hue} ${Math.round(clamped * 100)}% 58%)`;
}

export function useCoverColor(url: string | null | undefined): string | null {
  const [color, setColor] = useState<string | null>(null);

  useEffect(() => {
    setColor(null);
    if (!url) return;

    let cancelled = false;
    const image = new Image();
    // MUSS vor `src` gesetzt werden, sonst bleibt das Canvas "tainted".
    image.crossOrigin = 'anonymous';

    image.onload = () => {
      if (cancelled) return;
      try {
        const result = extract(image);
        if (!cancelled) setColor(result);
      } catch {
        // Kein CORS oder kein Canvas -- Signaturfarbe bleibt.
      }
    };

    image.src = url;

    return () => {
      cancelled = true;
    };
  }, [url]);

  return color;
}
